import React, { useState, useCallback, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { crashState } from '../utils/crashHandler';
import { useAuthState } from '../hooks/useAuthState';
import { useMutation } from 'convex/react';
import { api } from '@careercard/convex/convex/_generated/api';

type FeedbackCategory = 'bug' | 'feature_request' | 'improvement' | 'general' | 'other';

const FEEDBACK_CATEGORIES: { value: FeedbackCategory; label: string; description: string }[] = [
  {
    value: 'bug',
    label: 'Bug Report',
    description: 'Report something that isn\'t working correctly'
  },
  {
    value: 'feature_request',
    label: 'Feature Request',
    description: 'Suggest a new feature or capability'
  },
  {
    value: 'improvement',
    label: 'Improvement',
    description: 'Suggest an enhancement to existing functionality'
  },
  {
    value: 'general',
    label: 'General Feedback',
    description: 'Share your thoughts or suggestions'
  },
  {
    value: 'other',
    label: 'Other',
    description: 'Something else'
  }
];

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialContent?: string;
}

interface LogFile {
  file: File;
  preview: string;
}

const FeedbackModal: React.FC<FeedbackModalProps> = ({ isOpen, onClose, initialContent = '' }) => {
  const [feedback, setFeedback] = useState(initialContent);
  const [category, setCategory] = useState<FeedbackCategory>(crashState.value.log ? 'bug' : 'general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logFiles, setLogFiles] = useState<LogFile[]>([]);
  const { showToast } = useToast();
  const { user } = useAuth();
  useAuthState();
  const submitFeedbackMutation = useMutation(api.feedback.submitFeedback);

  // Reset form when modal opens with initial content
  useEffect(() => {
    if (isOpen) {
      setFeedback(initialContent);
      setCategory(crashState.value.log ? 'bug' : 'general');
      // Add crash log if available
      const currentCrash = crashState.value.log;
      if (currentCrash && !logFiles.length) {
        const crashLogFile = {
          file: new File(
            [JSON.stringify(currentCrash, null, 2)],
            `crash-${new Date().toISOString().replace(/[:.]/g, '-')}.log`,
            { type: 'text/plain' }
          ),
          preview: URL.createObjectURL(new Blob([JSON.stringify(currentCrash, null, 2)]))
        };
        setLogFiles([crashLogFile]);
      }
    }
  }, [isOpen, initialContent, logFiles.length]);

  // Cleanup previews when modal closes
  useEffect(() => {
    if (!isOpen) {
      logFiles.forEach(file => URL.revokeObjectURL(file.preview));
      setLogFiles([]);
      setFeedback(initialContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- logFiles intentionally omitted to prevent infinite loop when setLogFiles([]) is called
  }, [isOpen, initialContent]);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const newLogFiles = files.map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setLogFiles(prev => [...prev, ...newLogFiles]);
  }, []);

  const removeFile = useCallback((index: number) => {
    setLogFiles(prev => {
      const newFiles = [...prev];
      URL.revokeObjectURL(newFiles[index].preview);
      newFiles.splice(index, 1);
      return newFiles;
    });
  }, []);

  // Removed uploadFile function as we're now sending logs directly to the API

  // No longer needed - system info is automatically included in all feedback

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!feedback.trim()) {
      showToast('Please enter your feedback', 'error');
      return;
    }

    setIsSubmitting(true);
    try {
      // Prepare system info that was previously in Generate System Log
      const systemInfo = {
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        platform: navigator.platform,
        language: navigator.language,
        screenResolution: {
          width: window.screen.width,
          height: window.screen.height,
          pixelRatio: window.devicePixelRatio,
        },
        viewport: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        userId: user?.id,
        userEmail: user?.email,
        currentCrashLog: crashState.value.log,
        localStorage: {
          keys: Object.keys(localStorage),
          size: JSON.stringify(localStorage).length
        },
        url: window.location.href,
        performance: {
          memory: 'memory' in performance ? {
            jsHeapSizeLimit: (performance as { memory: { jsHeapSizeLimit: number } }).memory.jsHeapSizeLimit,
            totalJSHeapSize: (performance as { memory: { totalJSHeapSize: number } }).memory.totalJSHeapSize,
            usedJSHeapSize: (performance as { memory: { usedJSHeapSize: number } }).memory.usedJSHeapSize
          } : undefined,
          timing: performance.timing ? {
            navigationStart: performance.timing.navigationStart,
            loadEventEnd: performance.timing.loadEventEnd,
            domComplete: performance.timing.domComplete,
            domInteractive: performance.timing.domInteractive,
          } : undefined,
        }
      };

      // Prepare any uploaded log files
      const uploadedLogs = await Promise.all(
        logFiles.map(async ({ file }) => {
          const content = await file.text();
          return {
            name: file.name,
            content
          };
        })
      );

      // Always include system info as a log entry
      const logsData = [
        {
          name: `system-info-${new Date().toISOString().replace(/[:.]/g, '-')}.json`,
          content: systemInfo  // Send as object, not stringified
        },
        ...uploadedLogs
      ];

      // Get page context
      const pageContext = window.location.pathname;
      const userAgent = navigator.userAgent;
      const viewportSize = `${window.innerWidth}x${window.innerHeight}`;

      // Submit feedback via Convex
      await submitFeedbackMutation({
        category,
        message: feedback.trim(),
        pageContext,
        userAgent,
        viewportSize,
        logs: logsData,
        isCrashReport: Boolean(crashState.value.log),
      });

      showToast('Thank you for your feedback!', 'success');
      setFeedback('');
      setLogFiles([]);
      onClose();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      showToast('Failed to submit feedback. Please try again.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCrashReport = crashState.value.log !== null;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      className="relative z-[9999]"
    >
      <div className="fixed inset-0 z-[9998] bg-black/50" aria-hidden="true" />

      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-[95%] sm:max-w-[85%] md:max-w-2xl lg:max-w-3xl rounded-lg bg-white p-4 sm:p-6 shadow-xl dark:bg-gray-800">
          <Dialog.Title className="text-xl sm:text-2xl font-medium text-gray-900 dark:text-white mb-2">
            {isCrashReport ? 'Report Application Error' : 'Provide Feedback'}
          </Dialog.Title>
          
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-4">
            {isCrashReport
              ? 'We apologize for the error. Please help us improve by describing what happened. System information and error logs will be automatically included.'
              : 'Share your thoughts, suggestions, or report any issues you\'ve encountered. System information will be automatically included to help us better understand any problems.'}
          </p>
          
          <form onSubmit={handleSubmit} className="mt-4">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Feedback Type
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as FeedbackCategory)}
                className="w-full rounded-md border border-gray-300 p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 focus:outline-none disabled:opacity-50"
                disabled={isSubmitting || isCrashReport}
                aria-label="Feedback category"
              >
                {FEEDBACK_CATEGORIES.map(({ value, label, description }) => (
                  <option key={value} value={value}>
                    {label} - {description}
                  </option>
                ))}
              </select>
            </div>

            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={isCrashReport
                ? "Please describe what you were doing when the error occurred..."
                : "Type your feedback here..."}
              className="w-full rounded-md border border-gray-300 p-3 focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white text-base"
              rows={8}
              disabled={isSubmitting}
            />

            {/* File Upload Section */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {isCrashReport ? 'Error Logs (Automatically Attached)' : 'Attach Additional Log Files (Optional)'}
                </label>
                {!isCrashReport && (
                  <label className="inline-flex items-center px-3 py-1.5 bg-white dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-sm rounded-md border border-gray-300 dark:border-gray-600 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors">
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Select Files
                    <input
                      type="file"
                      className="hidden"
                      multiple
                      accept=".log,.txt,.json"
                      onChange={handleFileChange}
                      disabled={isSubmitting}
                    />
                  </label>
                )}
              </div>
              {logFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-md">
                  {logFiles.map((file, index) => (
                    <div 
                      key={file.preview}
                      className="inline-flex items-center bg-white dark:bg-gray-700 rounded px-2 py-1 text-sm"
                    >
                      <svg className="w-4 h-4 mr-1 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-gray-600 dark:text-gray-300 mr-2">
                        {file.file.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400 transition-colors"
                        disabled={isSubmitting}
                        aria-label={`Remove ${file.file.name}`}
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-gray-200 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="rounded-md bg-blue-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
};

export default FeedbackModal; 